/**
 * Catch Reveal Overlay
 * Shows a dramatic reveal card when the player catches a fish.
 * Quality-appropriate visual effects: glow borders, screen flash, shake, sparkles.
 * Auto-dismisses after 2s or tap to dismiss.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { FishInstance, FishDefinition, Quality, QualityColors, QualityNames } from '@/types';
import { formatWeight, formatLength } from '@/utils';

/** Data passed to the overlay when a fish is caught */
export interface CatchRevealData {
  fish: FishInstance;
  definition: FishDefinition;
  isNewRecord: boolean;
  streak: number;
  goldValue: number;
}

// --- Quality-based glow color configs (Tailwind-compatible hex) ---
const QUALITY_GLOW: Record<Quality, { border: string; shadow: string; bg: string }> = {
  [Quality.COMMON]:    { border: 'border-gray-400',   shadow: '',                                              bg: '' },
  [Quality.UNCOMMON]:  { border: 'border-green-400',  shadow: 'shadow-[0_0_20px_rgba(30,255,0,0.4)]',         bg: '' },
  [Quality.RARE]:      { border: 'border-blue-400',   shadow: 'shadow-[0_0_25px_rgba(0,112,221,0.5)]',        bg: '' },
  [Quality.EPIC]:      { border: 'border-purple-400', shadow: 'shadow-[0_0_30px_rgba(163,53,238,0.6)]',       bg: '' },
  [Quality.LEGENDARY]: { border: 'border-orange-400', shadow: 'shadow-[0_0_40px_rgba(255,128,0,0.7)]',        bg: '' },
  [Quality.UNIQUE]:    { border: 'border-yellow-300', shadow: 'shadow-[0_0_50px_rgba(255,215,0,0.8)]',        bg: '' },
  [Quality.QUEST]:     { border: 'border-cyan-300',   shadow: 'shadow-[0_0_20px_rgba(0,204,255,0.4)]',        bg: '' },
};

interface CatchRevealOverlayProps {
  data: CatchRevealData | null;
  onDismiss: () => void;
}

export function CatchRevealOverlay({ data, onDismiss }: CatchRevealOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setExiting(true);
    // Let exit animation play (300ms)
    exitTimerRef.current = setTimeout(() => {
      setVisible(false);
      setExiting(false);
      exitingRef.current = false;
      onDismiss();
    }, 300);
  }, [onDismiss]);

  useEffect(() => {
    if (data) {
      // Clear any lingering exit animation from previous catch
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      exitingRef.current = false;
      setVisible(true);
      setExiting(false);
      // Auto-dismiss after 2 seconds
      timerRef.current = setTimeout(dismiss, 2000);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [data, dismiss]);

  if (!visible || !data) return null;

  const { fish, definition, isNewRecord, streak, goldValue } = data;
  const quality = fish.quality;
  const glow = QUALITY_GLOW[quality];
  const qualityColor = QualityColors[quality];
  const qualityName = QualityNames[quality];

  // Quality tier index for conditional effects
  const qualityTiers: Quality[] = [
    Quality.COMMON, Quality.UNCOMMON, Quality.RARE,
    Quality.EPIC, Quality.LEGENDARY, Quality.UNIQUE,
  ];
  const tierIndex = qualityTiers.indexOf(quality);

  // Effects by tier
  const hasScalePulse = tierIndex >= 2;   // RARE+
  const hasScreenFlash = tierIndex >= 3;  // EPIC+
  const hasShake = tierIndex >= 4;        // LEGENDARY+
  const hasSparkle = tierIndex >= 5;      // UNIQUE

  return (
    <div
      className="absolute inset-0 z-40 flex items-end justify-center pointer-events-auto"
      onClick={dismiss}
    >
      {/* Screen flash for EPIC+ */}
      {hasScreenFlash && !exiting && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: qualityColor,
            opacity: 0,
            animation: 'catchFlash 0.4s ease-out forwards',
          }}
        />
      )}

      {/* Shake wrapper for LEGENDARY+ */}
      <div
        className={`w-full max-w-sm mx-4 mb-8 ${hasShake && !exiting ? 'catch-shake' : ''}`}
      >
        {/* Card */}
        <div
          className={`
            relative rounded-xl border-2 ${glow.border} ${glow.shadow}
            bg-gray-900/95 backdrop-blur-sm p-4
            ${exiting ? 'catch-card-exit' : 'catch-card-enter'}
            ${hasScalePulse && !exiting ? 'catch-pulse' : ''}
          `}
        >
          {/* Sparkle particles for UNIQUE */}
          {hasSparkle && !exiting && (
            <>
              <span className="catch-sparkle catch-sparkle-1" style={{ color: qualityColor }} />
              <span className="catch-sparkle catch-sparkle-2" style={{ color: qualityColor }} />
              <span className="catch-sparkle catch-sparkle-3" style={{ color: qualityColor }} />
              <span className="catch-sparkle catch-sparkle-4" style={{ color: qualityColor }} />
            </>
          )}

          {/* Pulsing glow ring for LEGENDARY+ */}
          {tierIndex >= 4 && !exiting && (
            <div
              className="absolute -inset-1 rounded-xl pointer-events-none"
              style={{
                boxShadow: `0 0 20px ${qualityColor}, 0 0 40px ${qualityColor}40`,
                animation: 'catchGlowPulse 1s ease-in-out infinite',
              }}
            />
          )}

          {/* Top row: emoji + name + quality */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-4xl">{definition.emoji || '🐟'}</span>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-white truncate">
                {definition.name}
              </h3>
              <span
                className="text-sm font-semibold"
                style={{ color: qualityColor }}
              >
                {qualityName}
              </span>
            </div>

            {/* Gold value */}
            <div className="text-right flex-shrink-0">
              <div className="text-yellow-400 font-bold text-lg">
                {goldValue.toFixed(0)}g
              </div>
            </div>
          </div>

          {/* Stats row: length + weight */}
          <div className="flex items-center gap-4 text-sm text-gray-300 mb-2">
            <span>
              {formatLength(fish.length)}
            </span>
            <span className="text-gray-600">|</span>
            <span>
              {formatWeight(fish.weight)}
            </span>
            {fish.isWild && (
              <>
                <span className="text-gray-600">|</span>
                <span className="text-emerald-400 font-medium">Wild</span>
              </>
            )}
          </div>

          {/* Badges row: NEW RECORD + streak */}
          {(isNewRecord || streak > 1) && (
            <div className="flex items-center gap-2">
              {isNewRecord && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-bold animate-pulse">
                  NEW RECORD!
                </span>
              )}
              {streak > 1 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold">
                  {streak}x Streak!
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inline keyframe styles */}
      <style>{`
        @keyframes catchSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes catchSlideDown {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(100%); opacity: 0; }
        }
        @keyframes catchFlash {
          0%   { opacity: 0.35; }
          100% { opacity: 0; }
        }
        @keyframes catchPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.03); }
        }
        @keyframes catchShake {
          0%, 100% { transform: translateX(0); }
          10%      { transform: translateX(-4px); }
          20%      { transform: translateX(4px); }
          30%      { transform: translateX(-3px); }
          40%      { transform: translateX(3px); }
          50%      { transform: translateX(-2px); }
          60%      { transform: translateX(2px); }
          70%      { transform: translateX(-1px); }
          80%      { transform: translateX(1px); }
        }
        @keyframes catchGlowPulse {
          0%, 100% { opacity: 0.5; }
          50%      { opacity: 1; }
        }
        @keyframes sparkleFloat {
          0%   { transform: translate(0, 0) scale(0); opacity: 1; }
          50%  { opacity: 1; }
          100% { transform: translate(var(--sx), var(--sy)) scale(1); opacity: 0; }
        }

        .catch-card-enter {
          animation: catchSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .catch-card-exit {
          animation: catchSlideDown 0.3s ease-in forwards;
        }
        .catch-pulse {
          animation: catchPulse 0.8s ease-in-out 1;
        }
        .catch-shake {
          animation: catchShake 0.5s ease-out 1;
        }

        .catch-sparkle {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          pointer-events: none;
          animation: sparkleFloat 1.2s ease-out infinite;
        }
        .catch-sparkle-1 { top: 10%; left: 15%; --sx: -20px; --sy: -30px; animation-delay: 0s; }
        .catch-sparkle-2 { top: 5%;  right: 20%; --sx: 25px;  --sy: -25px; animation-delay: 0.2s; }
        .catch-sparkle-3 { bottom: 15%; left: 10%; --sx: -15px; --sy: 20px; animation-delay: 0.4s; }
        .catch-sparkle-4 { bottom: 10%; right: 15%; --sx: 20px;  --sy: 25px; animation-delay: 0.6s; }
      `}</style>
    </div>
  );
}
